import { useEffect, useRef, useState } from "react";

interface UseThreadActivityTimeoutParams {
  threadId: string;
  status: string;
  phase: string;
  timeoutMs?: number;
  /** Wall-clock ms of the last streamed delta (#3162). While set, the budget
   *  is `heartbeatTimeoutMs` and every change restarts the timer: a reply that
   *  is still arriving is never "stuck", one that stalls is called out fast. */
  heartbeat?: number;
  heartbeatTimeoutMs?: number;
  /** Called once when the budget elapses, before `timedOut` flips. Typically an
   *  incremental resync: if the turn actually finished while a broadcast was
   *  lost, the resulting phase change resets the timer and the affordance
   *  never shows. */
  onTimeout?: () => unknown;
}

interface UseThreadActivityTimeoutResult {
  timedOut: boolean;
}

const DEFAULT_TIMEOUT_MS = import.meta.env.DEV ? 120_000 : 60_000;
// A queued turn is waiting for a worker, not for the model; queue contention
// behind generation jobs deserves more room before being called stuck.
export const QUEUED_TIMEOUT_MS = import.meta.env.DEV ? 180_000 : 90_000;

export function defaultTimeoutFor(phase: string): number {
  return phase === "queued" ? QUEUED_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
}

// A streaming reply whose deltas stop for this long is treated as stalled.
export const STREAM_STALL_TIMEOUT_MS = 15_000;

// Surfaces a "stuck" flag when a chat thread has held the same active
// (status, phase) longer than the budget. Each phase transition resets the
// timer — that's a heartbeat, the agent is still progressing. Only a single
// phase held continuously past the budget trips the flag.
//
// Pure render-time UI affordance. Does not mutate the chat store itself; the
// optional onTimeout callback is the caller's chance to refetch first.
export function useThreadActivityTimeout({
  threadId,
  status,
  phase,
  timeoutMs,
  heartbeat,
  heartbeatTimeoutMs,
  onTimeout,
}: UseThreadActivityTimeoutParams): UseThreadActivityTimeoutResult {
  const [timedOut, setTimedOut] = useState(false);
  const isActive = status === "active" && phase !== "idle";
  const budget =
    heartbeat !== undefined
      ? (heartbeatTimeoutMs ?? STREAM_STALL_TIMEOUT_MS)
      : (timeoutMs ?? defaultTimeoutFor(phase));
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new heartbeat deliberately restarts the timer
  useEffect(() => {
    if (!isActive) {
      setTimedOut(false);
      return;
    }

    setTimedOut(false);
    let cancelled = false;
    const startedAt = Date.now();
    const handle = setTimeout(() => {
      console.warn(
        `[chat] thread ${threadId} stuck in phase '${phase}' for ${Date.now() - startedAt}ms`,
      );
      const refresh = onTimeoutRef.current;
      if (!refresh) {
        setTimedOut(true);
        return;
      }
      // Refetch once before bothering the user. If the refetch changes the
      // phase this effect is torn down (cancelled) and the flag stays false.
      let pending: unknown;
      try {
        pending = refresh();
      } catch {
        pending = undefined;
      }
      Promise.resolve(pending)
        .catch(() => undefined)
        .then(() => {
          if (!cancelled) setTimedOut(true);
        });
    }, budget);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isActive, phase, threadId, budget, heartbeat]);

  return { timedOut };
}
